import { DestroyRef, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, EMPTY, forkJoin, map, Observable, ReplaySubject, take, tap } from 'rxjs';
import { IntegrationModel } from 'src/app/models/api';
import { PolicyMinimalModel } from 'src/app/models/api/governance/policy.model';
import { BaseViolationRule } from 'src/app/models/api/governance/violationRule.model';
import { PoliciesHttpService } from '../../httpServices/governance/policiesHttpService/policies-http.service';
import { ViolationRulesHttpService } from '../../httpServices/governance/violationRulesHttpService/violation-rules-http.service';
import { HttpErrorHandlerService } from '../../httpServices/httpErrorHandlerService/http-error-handler.service';
import { IntegrationsHttpService } from '../../httpServices/integrationsHttpsService/integrations-http.service';
import { LabelModel } from 'src/app/models/api/common';
import { LabelsHttpService } from '../../httpServices/labelsHttpService/labels-http.service';
import { JsonHalCollection } from 'src/app/models/api/jsonhal.model';
import { Platform } from 'src/app/models/api';
import {
  EffectivePolicySettingEnum,
  CategoryToRules,
} from 'src/app/services/infra/store/model/effectivePolicySetting.model';
import { EffectivePolicySettingsStore } from 'src/app/services/infra/store/effectivePolicySettingStore';
import { IViolationsViewModel } from 'src/app/models/viewModels/violationsViewModel';
import { ViolationModel } from 'src/app/models/api/violations.model';
import {
  CombinedIntegration,
  IntegrationsScansResponse,
  StatusCodeEnum,
  StatusReasonEnum,
} from 'src/app/models/api/integration.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class StoreService {
  public static isRefreshLabels = false;

  public integrations$ = new ReplaySubject<CombinedIntegration[]>(1);
  public labels$ = new ReplaySubject<LabelModel[]>(1);
  public accountMinimalPolicies$ = new ReplaySubject<PolicyMinimalModel[]>(1);
  public baseViolationRule$ = new ReplaySubject<BaseViolationRule[]>(1);

  private readonly _violations$ = new BehaviorSubject<IViolationsViewModel[]>([]);
  private readonly _hygiene$ = new BehaviorSubject<IViolationsViewModel[]>([]);
  private readonly _selectedViolation$ = new BehaviorSubject<ViolationModel | null>(null);

  /**
   * This is a map of integration id to integration name, this will be updated each time the integrations are updated
   */
  public integrationsNamesById: { [id: string]: string } = {};

  private _amountOfIntegrations = -1;
  private _effectivePolicySettingsStore: EffectivePolicySettingsStore;

  constructor(
    private readonly _integrationHttpService: IntegrationsHttpService,
    private readonly _violationRulesHttpService: ViolationRulesHttpService,
    private readonly _policyHttpService: PoliciesHttpService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _labelsHttpService: LabelsHttpService,
    private readonly _destroyRef: DestroyRef
  ) {
    this.updateIntegrations$().pipe(takeUntilDestroyed(this._destroyRef)).subscribe();
    this.refreshLabels();
    this._getDefaultViolationRules();
  }

  public refreshLabels() {
    // Every 5 minutes we will refresh the labels using updateLabels
    if (StoreService.isRefreshLabels) return;
    StoreService.isRefreshLabels = true;
    this.updateLabels(); // First time we will update the labels immediately
    const minutes = 5;
    setInterval(
      () => {
        this.updateLabels();
      },
      minutes * 60 * 1000
    );
  }

  public updateLabels() {
    this._labelsHttpService.getLabels().subscribe({
      next: (jsonHalLabels: JsonHalCollection<LabelModel>) => {
        this.labels$.next(jsonHalLabels._embedded.items);
      },
      error: (err) => {
        this._httpErrorHandlerService.handle(err);
        this.labels$.next([]);
      },
    });
  }

  public updateIntegrations$(): Observable<CombinedIntegration[]> {
    return forkJoin([
      this._integrationHttpService.getIntegrationsDetails$(),
      this._integrationHttpService.getIntegrationsScans$(),
    ]).pipe(
      map(([integrationsDetails, integrationsScansResponse]) =>
        this._combineIntegrationsDetailsAndScansData(integrationsDetails, integrationsScansResponse)
      ),
      map((combinedIntegrations) => this._sortIntegrations(combinedIntegrations)),
      tap((sortedIntegrations: CombinedIntegration[]) => {
        if (this._amountOfIntegrations === -1 || sortedIntegrations.length !== this._amountOfIntegrations) {
          this.updateAccountMinimalPolicies();
        }

        this.integrationsNamesById = sortedIntegrations.reduce(
          (map, integration: CombinedIntegration) => ({
            ...map,
            [integration.id]: integration.name,
          }),
          {}
        );

        this._amountOfIntegrations = sortedIntegrations.length;
        this.integrations$.next(sortedIntegrations);

        return sortedIntegrations;
      }),
      catchError((error) => {
        this._httpErrorHandlerService.handle(error);
        this.integrations$.next([]);

        return EMPTY;
      })
    );
  }

  public updateAccountMinimalPolicies() {
    this._policyHttpService.getPolicies().subscribe({
      next: (policies: PolicyMinimalModel[]) => {
        this.accountMinimalPolicies$.next(policies);
      },
      error: (err) => {
        this._httpErrorHandlerService.handle(err);
        this.accountMinimalPolicies$.next([]);
      },
    });
  }

  public getCategoryRulesByPlatformAndSettings(
    platform: Platform,
    effectivePolicySetting: EffectivePolicySettingEnum
  ): CategoryToRules[] {
    return (
      this._effectivePolicySettingsStore?.getCategoryRulesByPlatformAndSettings(platform, effectivePolicySetting) ?? []
    );
  }

  public getViolationsPageData$(): Observable<IViolationsViewModel[]> {
    return this._violations$.asObservable();
  }

  // Remove this method after refactoring ViolationsComponent and PrimeTableComponent so the latter does not accept BehSubject anymore
  public getViolationsPageDataAsSubject$(): BehaviorSubject<IViolationsViewModel[]> {
    return this._violations$;
  }

  public setViolationsPageData(violations: IViolationsViewModel[]): void {
    this._violations$.next(violations);
  }

  public getSelectedViolation$(): Observable<ViolationModel | null> {
    return this._selectedViolation$.asObservable();
  }

  public setSelectedViolation(violation: ViolationModel | null): void {
    this._selectedViolation$.next(violation);
  }

  public getHygienePageData$(): Observable<IViolationsViewModel[]> {
    return this._hygiene$.asObservable();
  }

  // Remove this method after refactoring HygieneComponent and PrimeTableComponent so the latter does not accept BehSubject anymore
  public getHygienePageDataAsSubject$(): BehaviorSubject<IViolationsViewModel[]> {
    return this._hygiene$;
  }

  public setHygienePageData(hygienePageData: IViolationsViewModel[]): void {
    this._hygiene$.next(hygienePageData);
  }

  public updatePageData(
    getData$: () => Observable<IViolationsViewModel[]>,
    setData: (data: IViolationsViewModel[]) => void,
    violationToUpdate: ViolationModel
  ): Observable<IViolationsViewModel[]> {
    return getData$().pipe(
      take(1),
      map((violations) =>
        violations.map((v) => (v.id === violationToUpdate.id ? { ...v, labels: violationToUpdate.labelBindings } : v))
      ),
      tap((updatedViolations) => setData(structuredClone(updatedViolations)))
    );
  }

  private _getDefaultViolationRules() {
    this._violationRulesHttpService.getBaseRules().subscribe((defaultRules) => {
      this.baseViolationRule$.next(defaultRules);
      this._effectivePolicySettingsStore = new EffectivePolicySettingsStore(defaultRules);
    });
  }

  // Sort integrations by their status (active > inactive > disabled) and later by their last update time
  private _sortIntegrations(integrations: CombinedIntegration[]) {
    return integrations.sort((integration1, integration2) => {
      const hasSameStatus =
        integration1.connectionStatus.code === integration2.connectionStatus.code &&
        integration1.connectionStatus.reason === integration2.connectionStatus.reason;

      if (hasSameStatus) {
        if (integration1.lastUpdated > integration2.lastUpdated) {
          return -1;
        } else {
          return 1;
        }
      }

      if (integration1.connectionStatus.code === StatusCodeEnum.on) {
        return -1;
      }
      if (integration2.connectionStatus.code === StatusCodeEnum.on) {
        return 1;
      }
      if (integration1.connectionStatus.reason === StatusReasonEnum.failing) {
        return -1;
      }
      return 1;
    });
  }

  private _combineIntegrationsDetailsAndScansData(
    integrationsDetails: IntegrationModel[],
    integrationsScansResponse: IntegrationsScansResponse
  ): CombinedIntegration[] {
    const combinedIntegrations: CombinedIntegration[] = [];

    integrationsDetails.map((integration) => {
      const scanInfo = integrationsScansResponse.integrationsScans?.find(
        (scan) => scan.integrationId === integration.id
      );

      const combined = {
        ...integration,
        lastScanEndedAt: scanInfo?.scan?.endTime,
        dataCollectionStartedAt: scanInfo?.dataCollection?.startTime,
      };

      combinedIntegrations.push(combined);
    });

    return combinedIntegrations;
  }
}
